export type Language = 'en' | 'ar';

export interface Translations {
    // Navigation
    nav: {
        products: string;
        governance: string;
        extension: string;
    };
    // Hero Section
    hero: {
        title: string;
        titleHighlight: string;
        subtitle: string;
        badgeGovernance: string;
        badgeExtension: string;
        btnExplore: string;
        btnInstall: string;
        featurePrivacy: string;
        featureTools: string;
        featureBilingual: string;
    };
    // Products Section
    products: {
        title: string;
        subtitle: string;
        governance: {
            title: string;
            desc: string;
            link: string;
        };
        extension: {
            title: string;
            desc: string;
            link: string;
        };
    };
    // Governance Engine Section
    governanceEngine: {
        badge: string;
        title: string;
        subtitle: string;
        features: {
            genericCore: { title: string; desc: string };
            pluggableRules: { title: string; desc: string };
            flexibleDeploy: { title: string; desc: string };
            clearOutcomes: { title: string; desc: string };
        };
    };
    // Chrome Extension Section
    chromeExtension: {
        badge: string;
        title: string;
        subtitle: string;
        btnAdd: string;
        howItWorks: string;
        steps: {
            install: { title: string; desc: string };
            navigate: { title: string; desc: string };
            command: { title: string; desc: string };
            done: { title: string; desc: string };
        };
        featuresTitle: string;
        features: {
            formAutomation: { title: string; desc: string };
            dataExtraction: { title: string; desc: string };
            chartCreation: { title: string; desc: string };
            worksAnywhere: { title: string; desc: string };
            bilingual: { title: string; desc: string };
            privacyFirst: { title: string; desc: string };
        };
    };
    // Footer
    footer: {
        description: string;
        copyright: string;
    };
}

export const translations: Record<Language, Translations> = {
    en: {
        nav: {
            products: 'Products',
            governance: 'Governance',
            extension: 'Extension',
        },
        hero: {
            title: 'AI Automation for ',
            titleHighlight: 'Real Work',
            subtitle: 'Orbit AI helps organizations automate governance, compliance, and everyday web tasks using configurable AI engines and natural language.',
            badgeGovernance: 'Governance Engine',
            badgeExtension: 'Chrome Extension',
            btnExplore: 'Explore Products',
            btnInstall: 'Install Extension',
            featurePrivacy: 'Privacy-focused',
            featureTools: 'Works with existing tools',
            featureBilingual: 'Bilingual (EN/AR)',
        },
        products: {
            title: 'Our Products',
            subtitle: 'Powerful AI solutions designed to transform how you work',
            governance: {
                title: 'Governance Engine',
                desc: 'AI-powered governance and compliance engine with configurable rule packs for your ERP and business data.',
                link: 'Learn More',
            },
            extension: {
                title: 'Chrome Extension',
                desc: 'Fill forms, extract data, and create charts on any website using simple English or Arabic commands.',
                link: 'Get Extension',
            },
        },
        governanceEngine: {
            badge: 'GOVERNANCE ENGINE',
            title: 'One Engine, Multiple Scenarios',
            subtitle: 'A generic AI engine that adapts to your governance and compliance needs',
            features: {
                genericCore: {
                    title: 'Generic Core',
                    desc: 'A powerful rules engine that ingests normalized data from ERPs, HR, finance, and other systems.',
                },
                pluggableRules: {
                    title: 'Pluggable Rules',
                    desc: 'Enable rule packs for audit, payroll, tax/VAT, or asset lifecycle without changing core.',
                },
                flexibleDeploy: {
                    title: 'Flexible Deploy',
                    desc: 'Runs on-premises or private cloud. Works with SQL Server and modern data stacks.',
                },
                clearOutcomes: {
                    title: 'Clear Outcomes',
                    desc: 'Detects anomalies, flags violations, generates explainable reports for decision-makers.',
                },
            },
        },
        chromeExtension: {
            badge: 'CHROME EXTENSION',
            title: 'Automate Web Tasks with AI',
            subtitle: 'Fill forms, extract data, and create charts using simple commands. No coding required.',
            btnAdd: 'Add to Chrome - Free',
            howItWorks: 'How It Works',
            steps: {
                install: { title: 'Install', desc: 'Add OrbitAI from Chrome Web Store' },
                navigate: { title: 'Navigate', desc: 'Go to any website you want to automate' },
                command: { title: 'Command', desc: 'Type what you want in English or Arabic' },
                done: { title: 'Done', desc: 'AI executes your command instantly' },
            },
            featuresTitle: 'Features',
            features: {
                formAutomation: { title: 'Form Automation', desc: 'Fill forms by describing what you want in plain language.' },
                dataExtraction: { title: 'Data Extraction', desc: 'Extract structured data from tables, lists, and any content.' },
                chartCreation: { title: 'Chart Creation', desc: 'Generate visual charts from data on any webpage.' },
                worksAnywhere: { title: 'Works Anywhere', desc: 'Compatible with any website, no integration needed.' },
                bilingual: { title: 'Bilingual', desc: 'Full support for English and Arabic commands.' },
                privacyFirst: { title: 'Privacy First', desc: 'Your data stays local. Nothing sent to external servers.' },
            },
        },
        footer: {
            description: 'Dubai-based AI company specializing in custom AI applications and intelligent automation solutions.',
            copyright: '2025 ORBIT AI. All rights reserved. | License No: 1223581',
        },
    },
    ar: {
        nav: {
            products: 'المنتجات',
            governance: 'الحوكمة',
            extension: 'الإضافة',
        },
        hero: {
            title: 'أتمتة الذكاء الاصطناعي ',
            titleHighlight: 'للعمل الحقيقي',
            subtitle: 'يساعد Orbit AI المؤسسات على أتمتة الحوكمة والامتثال ومهام الويب اليومية باستخدام محركات ذكاء اصطناعي قابلة للتكوين واللغة الطبيعية.',
            badgeGovernance: 'محرك الحوكمة',
            badgeExtension: 'إضافة كروم',
            btnExplore: 'استكشف المنتجات',
            btnInstall: 'تثبيت الإضافة',
            featurePrivacy: 'يركز على الخصوصية',
            featureTools: 'يعمل مع الأدوات الحالية',
            featureBilingual: 'ثنائي اللغة (EN/AR)',
        },
        products: {
            title: 'منتجاتنا',
            subtitle: 'حلول ذكاء اصطناعي قوية مصممة لتحويل طريقة عملك',
            governance: {
                title: 'محرك الحوكمة',
                desc: 'محرك حوكمة وامتثال مدعوم بالذكاء الاصطناعي مع حزم قواعد قابلة للتكوين لنظام ERP وبيانات الأعمال الخاصة بك.',
                link: 'اعرف المزيد',
            },
            extension: {
                title: 'إضافة كروم',
                desc: 'املأ النماذج واستخرج البيانات وأنشئ الرسوم البيانية على أي موقع باستخدام أوامر بسيطة بالعربية أو الإنجليزية.',
                link: 'احصل على الإضافة',
            },
        },
        governanceEngine: {
            badge: 'محرك الحوكمة',
            title: 'محرك واحد، سيناريوهات متعددة',
            subtitle: 'محرك ذكاء اصطناعي عام يتكيف مع احتياجات الحوكمة والامتثال الخاصة بك',
            features: {
                genericCore: {
                    title: 'النواة العامة',
                    desc: 'محرك قواعد قوي يستوعب البيانات الموحدة من أنظمة ERP والموارد البشرية والمالية وغيرها.',
                },
                pluggableRules: {
                    title: 'قواعد قابلة للتوصيل',
                    desc: 'تمكين حزم القواعد للتدقيق والرواتب والضرائب أو دورة حياة الأصول دون تغيير النواة.',
                },
                flexibleDeploy: {
                    title: 'نشر مرن',
                    desc: 'يعمل محلياً أو على السحابة الخاصة. متوافق مع SQL Server ومكدسات البيانات الحديثة.',
                },
                clearOutcomes: {
                    title: 'نتائج واضحة',
                    desc: 'يكتشف الشذوذ ويحدد المخالفات وينشئ تقارير قابلة للتفسير لصناع القرار.',
                },
            },
        },
        chromeExtension: {
            badge: 'إضافة كروم',
            title: 'أتمتة مهام الويب بالذكاء الاصطناعي',
            subtitle: 'املأ النماذج واستخرج البيانات وأنشئ الرسوم البيانية باستخدام أوامر بسيطة. لا حاجة للبرمجة.',
            btnAdd: 'أضف إلى كروم - مجاني',
            howItWorks: 'كيف يعمل',
            steps: {
                install: { title: 'التثبيت', desc: 'أضف OrbitAI من متجر كروم' },
                navigate: { title: 'التنقل', desc: 'اذهب إلى أي موقع تريد أتمتته' },
                command: { title: 'الأمر', desc: 'اكتب ما تريد بالعربية أو الإنجليزية' },
                done: { title: 'تم', desc: 'الذكاء الاصطناعي ينفذ أمرك فوراً' },
            },
            featuresTitle: 'المميزات',
            features: {
                formAutomation: { title: 'أتمتة النماذج', desc: 'املأ النماذج بوصف ما تريده بلغة بسيطة.' },
                dataExtraction: { title: 'استخراج البيانات', desc: 'استخرج بيانات منظمة من الجداول والقوائم وأي محتوى.' },
                chartCreation: { title: 'إنشاء الرسوم البيانية', desc: 'أنشئ رسوماً بيانية مرئية من البيانات على أي صفحة ويب.' },
                worksAnywhere: { title: 'يعمل في كل مكان', desc: 'متوافق مع أي موقع، لا حاجة للتكامل.' },
                bilingual: { title: 'ثنائي اللغة', desc: 'دعم كامل للأوامر بالعربية والإنجليزية.' },
                privacyFirst: { title: 'الخصوصية أولاً', desc: 'بياناتك تبقى محلية. لا شيء يُرسل لخوادم خارجية.' },
            },
        },
        footer: {
            description: 'شركة ذكاء اصطناعي مقرها دبي متخصصة في تطبيقات الذكاء الاصطناعي المخصصة وحلول الأتمتة الذكية.',
            copyright: '2025 ORBIT AI. جميع الحقوق محفوظة. | رقم الرخصة: 1223581',
        },
    },
};
